<template>
  <transition name="fade">
    <div v-if="isVisible" class="modal-overlay" @click="onClose">
      <div class="modal-content submissions-modal" @click.stop>
        <div class="modal-header">
          <h3>
            {{ t('competitions.submissionsFor') || 'Submissions for' }}:
            {{ competition && competition.name }}
          </h3>
          <button class="close-btn" @click="onClose">&times;</button>
        </div>

        <div class="modal-body">
          <div class="toolbar">
            <input
              type="text"
              v-model="searchQuery"
              class="search-input"
              :placeholder="t('general.search') || 'Search'"
            />

            <select v-model="sourceFilter" class="filter-select">
              <option value="all">{{ t('general.all') || 'All' }}</option>
              <option value="race">
                {{ t('competitions.competition') || 'Competition' }}
              </option>
              <option value="cup">{{ t('cups.cups') || 'Cup' }}</option>
            </select>

            <button class="btn btn-secondary" @click="toggleAllDetails">
              {{
                allExpanded
                  ? t('general.collapseAll') || 'Collapse All'
                  : t('general.expandAll') || 'Expand All'
              }}
            </button>

            <button
              class="btn btn-danger"
              :disabled="selectedCount === 0 || loading"
              @click="deleteSelected"
            >
              {{ t('general.deleteSelected') || 'Delete Selected' }} ({{
                selectedCount
              }})
            </button>

            <button class="btn btn-secondary" @click="exportCsv">
              {{ t('general.exportCsv') || 'Export CSV' }}
            </button>
          </div>

          <div v-if="loading" class="loading">
            {{ t('general.loading') || 'Loading...' }}
          </div>

          <div v-else class="table-wrap">
            <table class="submissions-table">
              <thead>
                <tr>
                  <th class="checkbox-header">
                    <input
                      type="checkbox"
                      :checked="allSelected"
                      @change="toggleSelectAll($event)"
                    />
                  </th>
                  <th>{{ t('general.id') || 'ID' }}</th>
                  <th>
                    {{
                      t('competitions.participantName') || 'Participant Name'
                    }}
                  </th>
                  <th>
                    {{
                      t('competitions.participantEmail') || 'Participant Email'
                    }}
                  </th>
                  <th>{{ t('general.source') || 'Source' }}</th>
                  <th class="date-cell-header">
                    {{ t('competitions.submittedAt') || 'Submitted At' }}
                  </th>
                  <th>{{ t('general.actions') || 'Actions' }}</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="s in filteredSubmissions" :key="s.id">
                  <tr>
                    <td class="checkbox-cell">
                      <input
                        type="checkbox"
                        :checked="isSelected(s.id)"
                        @change="toggleSelect(s.id, $event)"
                      />
                    </td>
                    <td class="mono">{{ formatIndex(s.id) }}</td>
                    <td>
                      {{
                        s.participant_name ||
                        t('general.notProvided') ||
                        'Not provided'
                      }}
                    </td>
                    <td class="mono">
                      {{
                        s.participant_email ||
                        t('general.notProvided') ||
                        'Not provided'
                      }}
                    </td>
                    <td>
                      <span class="source-pill" :class="s.source || 'race'">{{
                        (s.source || 'race').toUpperCase()
                      }}</span>
                    </td>
                    <td class="date-cell">
                      {{ formatDateTime(s.submitted_at) }}
                    </td>
                    <td class="actions-cell">
                      <div class="actions">
                        <button
                          class="btn btn-secondary btn-sm"
                          @click="toggleDetails(s)"
                        >
                          {{
                            t('competitions.submissionDetails') ||
                            'Submission Details'
                          }}
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="isExpanded(s.id)">
                    <td colspan="6" class="details-cell">
                      <div class="details">
                        <table class="kv-table">
                          <thead>
                            <tr>
                              <th>{{ t('general.field') || 'Field' }}</th>
                              <th>{{ t('general.value') || 'Value' }}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="(row, idx) in getAthleteFieldEntries(s)"
                              :key="idx"
                            >
                              <td class="kv-key">{{ row[0] }}</td>
                              <td class="kv-value">{{ row[1] }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-secondary" @click="onClose">
            {{ t('general.close') || 'Close' }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { ref, watch, computed } from 'vue'
import axios from 'axios'
import { useI18n } from 'vue-i18n'

export default {
  name: 'SubmissionsList',
  props: {
    isVisible: { type: Boolean, required: true },
    competition: { type: Object, required: false },
  },
  emits: ['close', 'deleted-count'],
  setup(props, { emit }) {
    const { t } = useI18n()
    const loading = ref(false)
    const submissions = ref([])
    const searchQuery = ref('')
    const sourceFilter = ref('all') // all | race | cup
    const expandedIds = ref(new Set())
    const selectedIds = ref(new Set())

    const fetchSubmissions = async () => {
      if (!props.competition || !props.competition.id) return
      loading.value = true
      try {
        const res = await axios.get(
          `/registration/competition/${props.competition.id}/submissions`
        )
        submissions.value = Array.isArray(res.data) ? res.data : []
      } catch (e) {
        console.error('Error fetching submissions:', e)
        submissions.value = []
      } finally {
        loading.value = false
      }
    }

    watch(
      () => [props.isVisible, props.competition && props.competition.id],
      async ([visible]) => {
        if (visible) {
          await fetchSubmissions()
        }
      }
    )

    const filteredSubmissions = computed(() => {
      const q = searchQuery.value.trim().toLowerCase()
      return (
        submissions.value
          .filter((s) =>
            sourceFilter.value === 'all'
              ? true
              : (s.source || 'race') === sourceFilter.value
          )
          .filter((s) => {
            if (!q) return true
            const hay = `${s.id}\n${s.participant_name || ''}\n${
              s.participant_email || ''
            }`.toLowerCase()
            return hay.includes(q)
          })
          // Sort ascending by submission id
          .sort((a, b) => Number(a.id) - Number(b.id))
          .map((s) => ({ ...s }))
      )
    })

    const expandedSubmissions = computed(() =>
      submissions.value.filter((s) => expandedIds.value.has(s.id))
    )

    const toggleDetails = (s) => {
      const next = new Set(expandedIds.value)
      if (next.has(s.id)) {
        next.delete(s.id)
      } else {
        next.add(s.id)
      }
      expandedIds.value = next
    }

    const isExpanded = (id) => expandedIds.value.has(id)

    const isSelected = (id) => selectedIds.value.has(id)
    const selectedCount = computed(() => selectedIds.value.size)
    const allSelected = computed(() => {
      const idsInView = filteredSubmissions.value.map((s) => s.id)
      if (idsInView.length === 0) return false
      for (const id of idsInView) {
        if (!selectedIds.value.has(id)) return false
      }
      return true
    })
    const toggleSelect = (id, evt) => {
      const next = new Set(selectedIds.value)
      if (evt && evt.target && evt.target.checked) {
        next.add(id)
      } else {
        next.delete(id)
      }
      selectedIds.value = next
    }
    const toggleSelectAll = (evt) => {
      const next = new Set(selectedIds.value)
      const idsInView = filteredSubmissions.value.map((s) => s.id)
      if (evt && evt.target && evt.target.checked) {
        idsInView.forEach((id) => next.add(id))
      } else {
        idsInView.forEach((id) => next.delete(id))
      }
      selectedIds.value = next
    }
    const deleteSelected = async () => {
      if (!props.competition || !props.competition.id) return
      if (selectedIds.value.size === 0) return
      if (
        !confirm(
          t('competitions.confirmDeleteSubmission') ||
            'Delete selected submissions?'
        )
      )
        return
      const ids = Array.from(selectedIds.value)
      try {
        // delete sequentially to keep it simple and avoid rate limits
        for (const id of ids) {
          await axios.delete(
            `/registration/competition/${props.competition.id}/submission/${id}`
          )
          submissions.value = submissions.value.filter((x) => x.id !== id)
          expandedIds.value.delete(id)
          selectedIds.value.delete(id)
        }
        emit('deleted-count', ids.length)
      } catch (e) {
        console.error('Error deleting selected submissions:', e)
      }
    }

    const allExpanded = computed(() => {
      const idsInView = filteredSubmissions.value.map((s) => s.id)
      if (idsInView.length === 0) return false
      for (const id of idsInView) {
        if (!expandedIds.value.has(id)) return false
      }
      return true
    })

    const toggleAllDetails = () => {
      const idsInView = filteredSubmissions.value.map((s) => s.id)
      if (idsInView.length === 0) return
      if (allExpanded.value) {
        const next = new Set(expandedIds.value)
        idsInView.forEach((id) => next.delete(id))
        expandedIds.value = next
      } else {
        const next = new Set(expandedIds.value)
        idsInView.forEach((id) => next.add(id))
        expandedIds.value = next
      }
    }

    const prettyJson = (obj) => {
      try {
        return JSON.stringify(
          obj && typeof obj === 'string' ? JSON.parse(obj) : obj,
          null,
          2
        )
      } catch {
        return JSON.stringify(obj, null, 2)
      }
    }

    const parseSubmissionData = (data) => {
      try {
        const parsed = typeof data === 'string' ? JSON.parse(data) : data
        return parsed && typeof parsed === 'object' ? parsed : {}
      } catch {
        return {}
      }
    }

    const flattenObject = (obj, prefix = '', result = {}) => {
      if (!obj || typeof obj !== 'object') return result
      for (const [key, value] of Object.entries(obj)) {
        const fullKey = prefix ? `${prefix}.${key}` : key
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          flattenObject(value, fullKey, result)
        } else {
          result[fullKey] = Array.isArray(value) ? JSON.stringify(value) : value
        }
      }
      return result
    }

    const formatDateTime = (iso) => {
      if (!iso) return ''
      const d = new Date(iso)
      return d.toLocaleString('et-EE')
    }

    const deleteSubmission = async (s) => {
      if (!props.competition || !props.competition.id) return
      if (
        !confirm(
          t('competitions.confirmDeleteSubmission') || 'Delete this submission?'
        )
      )
        return
      try {
        await axios.delete(
          `/registration/competition/${props.competition.id}/submission/${s.id}`
        )
        submissions.value = submissions.value.filter((x) => x.id !== s.id)
        expandedIds.value.delete(s.id)
        emit('deleted-count', 1)
      } catch (e) {
        console.error('Error deleting submission:', e)
      }
    }

    const toDisplayString = (value) => {
      if (value === null || value === undefined) return ''
      if (typeof value === 'object') return JSON.stringify(value)
      return String(value)
    }

    // Replace raw database IDs with sequential indices based on ascending order
    const formatIndex = (id) => {
      const ordered = [...filteredSubmissions.value]
        .sort((a, b) => Number(a.id) - Number(b.id))
        .map((s) => s.id)
      const idx = ordered.indexOf(id)
      return idx >= 0 ? idx + 1 : id
    }

    const getAthleteFieldEntries = (s) => {
      // Parse submission_data, prefer athlete fields if present
      const data = parseSubmissionData(s.submission_data)
      // If data contains top-level athlete-like fields, use those; otherwise, use flattened data
      const flattened = flattenObject(data)

      // Prefer a curated subset of athlete-related keys (common patterns)
      const preferredKeys = [
        'name',
        'fullName',
        'firstName',
        'lastName',
        'email',
        'phone',
        'gender',
        'birthDate',
        'nationality',
        'club',
        'team',
        'category',
        'distance',
      ]

      const entries = []

      // Add preferred keys that exist
      for (const key of preferredKeys) {
        if (flattened[key] !== undefined) {
          entries.push([key, toDisplayString(flattened[key])])
        }
      }

      // Add remaining keys, excluding arrays of athletes or teamManager objects already flattened
      const added = new Set(entries.map((e) => e[0]))
      for (const [k, v] of Object.entries(flattened)) {
        if (added.has(k)) continue
        // Skip internal/grouping keys
        if (k === 'athletes') continue
        entries.push([k, toDisplayString(v)])
      }

      return entries
    }

    const quoteCsv = (val) => {
      const v = String(val)
      if (v.includes(',') || v.includes('"') || v.includes('\n')) {
        return '"' + v.replace(/\"/g, '""') + '"'
      }
      return v
    }

    const exportCsv = () => {
      // Base columns available from the submission record
      const baseHeaders = [
        'id',
        // 'competition_id',
        // 'cup_id',
        // 'registration_form_id',
        // 'competition_name',
        // 'form_name',
        // 'form_title',
        // 'participant_name',
        // 'participant_email',
        'source',
        'submitted_at',
        // 'ip_address',
        // 'user_agent',
      ]

      // Collect dynamic keys from submission_data across all filtered submissions
      const dynamicKeySet = new Set()
      const flattenedById = new Map()
      for (const s of filteredSubmissions.value) {
        const flat = flattenObject(parseSubmissionData(s.submission_data))
        flattenedById.set(s.id, flat)
        Object.keys(flat).forEach((k) => dynamicKeySet.add(k))
      }

      const dynamicHeaders = Array.from(dynamicKeySet).sort()

      // Final headers list
      const headers = [...baseHeaders, ...dynamicHeaders]

      const cellValue = (s, key) => {
        switch (key) {
          case 'id':
            return s.id
          // case 'competition_id':
          //   return s.competition_id == null ? '' : s.competition_id
          // case 'cup_id':
          //   return s.cup_id == null ? '' : s.cup_id
          // case 'registration_form_id':
          //   return s.registration_form_id == null ? '' : s.registration_form_id
          // case 'competition_name':
          //   return s.competition_name == null ? '' : s.competition_name
          // case 'form_name':
          //   return s.form_name == null ? '' : s.form_name
          // case 'form_title':
          //   return s.form_title == null ? '' : s.form_title
          // case 'participant_name':
          //   return s.participant_name == null ? '' : s.participant_name
          // case 'participant_email':
          //   return s.participant_email == null ? '' : s.participant_email
          case 'source':
            return s.source || (s.cup_id ? 'cup' : 'race')
          case 'submitted_at':
            return s.submitted_at == null ? '' : s.submitted_at
          // case 'ip_address':
          //   return s.ip_address == null ? '' : s.ip_address
          // case 'user_agent':
          //   return s.user_agent == null ? '' : s.user_agent
          default: {
            const flat = flattenedById.get(s.id) || {}
            const val = flat[key]
            if (val === null || val === undefined) return ''
            if (typeof val === 'object') return JSON.stringify(val)
            return String(val)
          }
        }
      }

      const rows = filteredSubmissions.value.map((s) =>
        headers.map((h) => quoteCsv(cellValue(s, h)))
      )

      const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join(
        '\n'
      )
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `submissions_${
        (props.competition && props.competition.id) || 'export'
      }.csv`
      link.click()
      URL.revokeObjectURL(url)
    }

    const onClose = () => emit('close')

    return {
      t,
      loading,
      searchQuery,
      sourceFilter,
      submissions,
      filteredSubmissions,
      expandedSubmissions,
      toggleDetails,
      isExpanded,
      isSelected,
      selectedCount,
      allSelected,
      toggleSelect,
      toggleSelectAll,
      deleteSelected,
      allExpanded,
      toggleAllDetails,
      prettyJson,
      formatDateTime,
      formatIndex,
      deleteSubmission,
      getAthleteFieldEntries,
      exportCsv,
      onClose,
    }
  },
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}
.modal-content.submissions-modal {
  background: white;
  border-radius: 12px;
  width: 95%;
  max-width: 1000px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
}
.modal-body {
  padding: 0.75rem 1rem;
  overflow: auto;
}
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-top: 1px solid #dee2e6;
}
.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
}
.toolbar {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
}
.search-input {
  flex: 1 1 240px;
  padding: 0.5rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
}
.filter-select {
  padding: 0.5rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
}
.submissions-table {
  width: 100%;
  border-collapse: collapse;
}
.submissions-table th {
  background: #f8f9fa;
  text-align: left;
  padding: 0.5rem;
  font-weight: 600;
  font-size: 0.85rem;
  border-bottom: 1px solid #dee2e6;
}
.submissions-table th:last-child {
  text-align: right;
}
.submissions-table td {
  padding: 0.5rem;
  border-bottom: 1px solid #f1f3f5;
  vertical-align: top;
}
.submissions-table td:last-child {
  text-align: right;
}
.submissions-table td:last-child .actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}
.submissions-table .mono {
  font-family: ui-monospace, Menlo, Monaco, Consolas, 'Liberation Mono',
    'Courier New', monospace;
  font-size: 0.85rem;
}
.date-cell {
  white-space: nowrap;
  color: #6c757d;
  font-size: 0.85rem;
  text-align: right;
}
.date-cell-header {
  text-align: right !important;
}
.source-pill {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}
.source-pill.race {
  background: #e2e3e5;
  color: #495057;
}
.source-pill.cup {
  background: #cce5ff;
  color: #004085;
}
.details-cell {
  background: #f8f9fa;
}
.details {
  padding: 0.75rem;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  background: #fff;
}
.kv-table {
  width: 100%;
  border-collapse: collapse;
}
.kv-table thead th {
  text-align: left;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  padding: 0.4rem 0.5rem;
  font-size: 0.8rem;
}
.kv-table td {
  border-bottom: 1px solid #f1f3f5;
  padding: 0.4rem 0.5rem;
  vertical-align: top;
}
.kv-key {
  width: 30%;
  font-weight: 600;
  color: #495057;
}
.kv-value {
  width: 70%;
  color: #212529;
  word-break: break-word;
}
.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.6rem;
  border: none;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
}
.btn-secondary {
  background: #6c757d;
  color: #fff;
}
.btn-danger {
  background: #dc3545;
  color: #fff;
}
.btn-sm {
  padding: 0.2rem 0.4rem;
  font-size: 0.7rem;
}
.loading {
  padding: 1rem;
  color: #6c757d;
}
</style>
